import axios from "axios";
import constants from "../../../constants";
import { AutoShipment, BackorderedKit, Invoice, PendingApprovalKit, PendingDetails, RefillRequest, RefItem, ShipmentHistoryItem, UpcomingShipment } from "./types";
import { MedicationsMetadata } from "../../../types";
import { CustomerState } from "../../../redux/customer";
import { ChildCustomerState } from "../../../redux/childCustomer";

const getUpcomingShipments = async (customerNumber: string) => {
  const res = await axios.get(`${constants.ONTRAQ_API_URL}/api2/${customerNumber}/shipments/getShipments`);
  const upcomingShipments: Record<number, UpcomingShipment[]>  = res.data

  return upcomingShipments
};

const getMedicationMetadata = async (customerNumber: string): Promise<MedicationsMetadata> => {
  const res = await axios.get(`${constants.ONTRAQ_API_URL}/api2/${customerNumber}/medications/metadata`);
  return res.data as MedicationsMetadata
};

const getAutoShipments = async (customerNumber: string): Promise<AutoShipment[]> => {
  try {
    const res = await axios.get(`${constants.SUBSCRIPTION_SERVICE_URL}/api/Forecast/customer/${customerNumber}`);
    return res.data as AutoShipment[]
  } catch (err) {
    console.error(err);
    return [];
  }
};

const getBackorderedKits = async (customerNumber: string): Promise<{ [Identifier: string]: BackorderedKit }> => {
  const res = await axios.get(`${constants.ONTRAQ_API_URL}/api2/${customerNumber}/shipments/getBackorders`);
  return res.data as { [Identifier: string]: BackorderedKit };
};

const getPendingApprovals = async (customerNumber: string): Promise<PendingApprovalKit[]> => {
  const scope = ['pendingRefillApprovals'];
  const query = {
    where: {
      refill_status: 2,
      pending_approval: 1
    }
  };

  try {
    if (!customerNumber) {
      const error = new Error('There is no customer number');
      throw error
    }

    const res = await axios.get(`${constants.ONTRAQ_API_URL}/api2/${customerNumber}/kits?query=${JSON.stringify(query)}&scope=${JSON.stringify(scope)}`);
    return res.data as PendingApprovalKit[];
  } catch (err) {
    console.error(err);
    throw err;
  }
}

const getPendingDetails = async (kit: PendingApprovalKit): Promise<PendingDetails> => {
  const kitDetails = {
    kit_id: kit.kit_id,
    gp_kit_id: kit.GP_KitId
  };

  try {
    const res = await axios.post(`${constants.ONTRAQ_API_URL}/api2/${kit.customer_number}/kits/getPendingDetails`, kitDetails);
    return res.data as PendingDetails;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

const getShipmentHistory = async (customerNumber: string, query?: string): Promise<ShipmentHistoryItem[]> => {
  try {
    const queryString = query ? `?${query}` : '';
    const res = await axios.get(`${constants.ONTRAQ_API_URL}/api2/${customerNumber}/shipments/getShipmentHistory${queryString}`);
    return res.data as ShipmentHistoryItem[];
  } catch (err) {
    console.error(err);
    throw err;
  }
};

const getTaxRate = async (customer: CustomerState | ChildCustomerState) => {
  const data = {
    street: customer.street,
    city: customer.city,
    state: customer.state,
    zip: customer.zip
  }

  const res = await axios.post(`${constants.ONTRAQ_API_URL}/api2/${customer.customer_number}/kits/getTaxRate`, data);
  return res.data.data as number
}

const getTrackTrace = async (invoiceNumber: string) => {
  try {
    if (!invoiceNumber) {
      const error = new Error('There is no invoice number');
      throw error
    }

    const res = await axios.get(`${constants.ONTRAQ_API_URL}/api2/track-trace/${invoiceNumber}/get-invoice`);
    return res.data as Invoice;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

const sendRefillRequest = async (request: RefillRequest, customerNumber: string | undefined) => {
  try {
    if (!customerNumber) {
      const error = new Error('There is no customer number');
      throw error
    }

    await axios.post(`${constants.ONTRAQ_API_URL}/api2/${customerNumber}/kits/approveItems`, request);
    return true;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

const getRefItems = async (customerNumber: string, autoShipments: AutoShipment[]) => {
  const itemIds = autoShipments.map((item) => item.itemId)
  const res = await axios.get(`${constants.ONTRAQ_API_URL}/api2/${customerNumber}/items?ids=${JSON.stringify(itemIds)}`)
  const refItems: RefItem[] = res.data;

  return refItems
}

export {
  getUpcomingShipments,
  getMedicationMetadata,
  getAutoShipments,
  getBackorderedKits,
  getPendingApprovals,
  getPendingDetails,
  getShipmentHistory,
  getTaxRate,
  getTrackTrace,
  sendRefillRequest,
  getRefItems
}